import { useEffect, useRef, useState } from "react";
import { api, downscaleImage, errMsg, fileUrl, fmtDate } from "@/lib/api";
import { EmptyState, PageHeader, Spinner } from "@/components/bits";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { SearchableSelect } from "@/components/SearchableSelect";
import { Download, Eye, ImagePlus, Loader2, Megaphone, Pencil, Plus, Search, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const DISPLAY_LABEL = { todos: "Todos", eql: "EQL", bloco: "Bloco" };
const IMAGE_MODE_LABEL = { include: "Incluir imagem no Aviso", download: "Apenas Download" };

const EMPTY_FORM = {
  title: "", text: "", active: true, valid_from: "", valid_until: "",
  image_path: null, image_mode: "include", display: "todos", eqls: [],
};

const eqlLabel = (e) => [e?.numero, e?.descricao].filter(Boolean).join(" — ") || e?.id || "";

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toIso(localValue) {
  return localValue ? new Date(localValue).toISOString() : "";
}

async function downscaleImageRemoved() {}

export default function NoticesPage() {
  const [notices, setNotices] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [toggling, setToggling] = useState(null);
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [filterStatus, setFilterStatus] = useState("todas");
  const [eqlPickerOpen, setEqlPickerOpen] = useState(false);
  const [filterEql, setFilterEql] = useState(null);
  const [eqlFilterOpen, setEqlFilterOpen] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/tenant/notices").then(({ data }) => setNotices(data)).catch(() => setNotices([]));
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM, valid_from: toLocalInput(new Date().toISOString()) });
    setOpen(true);
  };

  const openEdit = (n) => {
    setEditing(n);
    setForm({
      title: n.title, text: n.text, active: n.active,
      valid_from: toLocalInput(n.valid_from), valid_until: toLocalInput(n.valid_until),
      image_path: n.image_path, image_mode: n.image_mode, display: n.display, eqls: n.eqls || [],
    });
    setOpen(true);
  };

  const pickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const optimized = await downscaleImage(file);
      const fd = new FormData();
      fd.append("file", optimized);
      const { data } = await api.post("/upload", fd);
      setForm((f) => ({ ...f, image_path: data.storage_path }));
      toast.success("Imagem anexada");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      title: form.title, text: form.text, active: form.active,
      valid_from: toIso(form.valid_from), valid_until: toIso(form.valid_until),
      image_path: form.image_path, image_mode: form.image_mode, display: form.display, eqls: form.eqls,
    };
    try {
      if (editing) {
        await api.patch(`/tenant/notices/${editing.id}`, payload);
        toast.success("Aviso atualizado");
      } else {
        await api.post("/tenant/notices", payload);
        toast.success("Aviso publicado");
      }
      setOpen(false);
      setEditing(null);
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (n) => {
    setToggling(n.id);
    try {
      await api.patch(`/tenant/notices/${n.id}`, { active: !n.active });
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setToggling(null);
    }
  };

  const deleteNotice = async () => {
    setDeleting(true);
    try {
      await api.delete(`/tenant/notices/${deleteTarget.id}`);
      toast.success("Aviso excluído");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setDeleting(false);
    }
  };

  const downloadImage = async (n) => {
    try {
      const { data } = await api.get(`/files/${n.image_path}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `aviso-${n.title.replace(/\s+/g, "-").toLowerCase()}.png`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const filtered = (notices || []).filter((n) => {
    if (filterStatus === "ativas" && !n.active) return false;
    if (filterStatus === "inativas" && n.active) return false;
    if (filterEql && !(n.display === "eql" && (n.eqls || []).some((e) => e.id === filterEql.id))) return false;
    if (filterFrom && new Date(n.valid_until) < new Date(`${filterFrom}T00:00:00`)) return false;
    if (filterTo && new Date(n.valid_from) > new Date(`${filterTo}T23:59:59`)) return false;
    return true;
  });

  if (!notices) return <Spinner />;

  return (
    <div data-testid="notices-page">
      <PageHeader title="Mural de Avisos" subtitle="Publique comunicados com imagem e período de validade">
        <Button data-testid="new-notice-button" onClick={openNew}
          className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition-colors duration-200">
          <Plus className="h-4 w-4 mr-1.5" /> Novo Aviso
        </Button>
      </PageHeader>

      {notices.length > 0 && (
        <div className="flex flex-wrap items-end gap-3 mb-4" data-testid="notice-filters">
          <div>
            <Label className="text-xs text-zinc-400">Data inicial</Label>
            <Input type="date" data-testid="notice-filter-from" value={filterFrom}
              onChange={(e) => setFilterFrom(e.target.value)}
              className="mt-1 bg-zinc-900 border-zinc-800 text-zinc-100 rounded-md w-40 [color-scheme:dark]" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Data final</Label>
            <Input type="date" data-testid="notice-filter-to" value={filterTo}
              onChange={(e) => setFilterTo(e.target.value)}
              className="mt-1 bg-zinc-900 border-zinc-800 text-zinc-100 rounded-md w-40 [color-scheme:dark]" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Status</Label>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger data-testid="notice-filter-status" className="mt-1 bg-zinc-900 border-zinc-800 text-zinc-100 rounded-md w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                <SelectItem data-testid="notice-filter-status-todas" value="todas">Todas</SelectItem>
                <SelectItem data-testid="notice-filter-status-ativas" value="ativas">Ativas</SelectItem>
                <SelectItem data-testid="notice-filter-status-inativas" value="inativas">Inativas</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-zinc-400">EQL</Label>
            <button type="button" data-testid="notice-filter-eql-button" onClick={() => setEqlFilterOpen(true)}
              className="mt-1 flex items-center justify-between gap-2 w-44 h-9 px-3 text-sm bg-zinc-900 border border-zinc-800 rounded-md text-left hover:border-indigo-500/60 transition-colors duration-200">
              <span className={`truncate ${filterEql ? "text-zinc-100" : "text-zinc-500"}`}>{filterEql ? eqlLabel(filterEql) : "Todas"}</span>
              {filterEql ? (
                <X data-testid="notice-filter-eql-clear" className="h-3.5 w-3.5 text-zinc-500 hover:text-rose-400 shrink-0"
                  onClick={(e) => { e.stopPropagation(); setFilterEql(null); }} />
              ) : (
                <Search className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
              )}
            </button>
          </div>
          {(filterFrom || filterTo || filterStatus !== "todas" || filterEql) && (
            <Button variant="ghost" data-testid="notice-filter-clear"
              onClick={() => { setFilterFrom(""); setFilterTo(""); setFilterStatus("todas"); setFilterEql(null); }}
              className="text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 rounded-md">
              Limpar
            </Button>
          )}
        </div>
      )}

      {notices.length === 0 ? (
        <EmptyState icon={Megaphone} title="Nenhum aviso publicado"
          subtitle="Crie avisos com texto, imagem e período de exibição para o mural." />
      ) : filtered.length === 0 ? (
        <p className="text-sm text-zinc-500 py-8 text-center" data-testid="notices-filtered-empty">Nenhum aviso encontrado para os filtros selecionados.</p>
      ) : (
        <div className="bg-zinc-900 border border-zinc-800 rounded-md overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="border-b border-zinc-800 text-left">
                <th className="px-4 py-3 text-xs font-medium text-zinc-400">Ativo</th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-400">Título</th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-400">Texto</th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-400">Início</th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-400">Fim</th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-400">Exibição</th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-400 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              <TooltipProvider delayDuration={200}>
                {filtered.map((n) => {
                  const expired = new Date(n.valid_until) < new Date();
                  return (
                    <tr key={n.id} data-testid={`notice-row-${n.id}`}
                      className="border-b border-zinc-800/60 hover:bg-zinc-800/50 transition-colors duration-200">
                      <td className="px-4 py-3">
                        <Switch data-testid={`notice-active-toggle-${n.id}`} checked={n.active}
                          disabled={toggling === n.id} onCheckedChange={() => toggleActive(n)}
                          title={n.active ? "Revogar aviso" : "Reativar aviso"} />
                      </td>
                      <td className="px-4 py-3 text-zinc-100 font-medium max-w-48 truncate" data-testid={`notice-title-${n.id}`}>{n.title}</td>
                      <td className="px-4 py-3 max-w-64">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="text-zinc-400 text-xs truncate block cursor-help" data-testid={`notice-text-excerpt-${n.id}`}>
                              {n.text.length > 60 ? `${n.text.slice(0, 60)}...` : n.text}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-xs whitespace-pre-wrap bg-zinc-800 border-zinc-700 text-zinc-100 text-xs">
                            {n.text}
                          </TooltipContent>
                        </Tooltip>
                      </td>
                      <td className="px-4 py-3 text-zinc-400 text-xs whitespace-nowrap" data-testid={`notice-valid-from-${n.id}`}>{fmtDate(n.valid_from)}</td>
                      <td className={`px-4 py-3 text-xs whitespace-nowrap ${expired ? "text-rose-400 font-medium" : "text-zinc-400"}`}
                        data-testid={`notice-valid-until-${n.id}`} title={expired ? "Validade expirada" : undefined}>
                        {fmtDate(n.valid_until)}
                      </td>
                      <td className="px-4 py-3 text-zinc-400 text-xs" data-testid={`notice-display-${n.id}`}>
                        {n.display === "eql" ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-help underline decoration-dotted underline-offset-2 decoration-zinc-600 hover:text-zinc-200 transition-colors duration-150"
                                data-testid={`notice-display-eql-trigger-${n.id}`}>
                                EQL ({n.eqls?.length || 0})
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs bg-zinc-800 border-zinc-700 text-zinc-100 text-xs"
                              data-testid={`notice-display-eql-list-${n.id}`}>
                              {n.eqls?.length ? (
                                <ul className="space-y-0.5 max-h-48 overflow-y-auto">
                                  {n.eqls.map((e) => <li key={e.id}>{eqlLabel(e)}</li>)}
                                </ul>
                              ) : "Nenhuma EQL selecionada"}
                            </TooltipContent>
                          </Tooltip>
                        ) : DISPLAY_LABEL[n.display]}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="inline-flex items-center gap-1">
                          <Button data-testid={`notice-edit-${n.id}`} variant="ghost" size="icon" title="Editar aviso"
                            onClick={() => openEdit(n)}
                            className="h-8 w-8 text-zinc-400 hover:text-indigo-400 hover:bg-zinc-800">
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button data-testid={`notice-view-${n.id}`} variant="ghost" size="icon" title="Visualizar aviso"
                            onClick={() => setViewing(n)}
                            className="h-8 w-8 text-zinc-400 hover:text-indigo-400 hover:bg-zinc-800">
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button data-testid={`notice-delete-${n.id}`} variant="ghost" size="icon" title="Excluir aviso"
                            onClick={() => setDeleteTarget(n)}
                            className="h-8 w-8 text-zinc-400 hover:text-rose-400 hover:bg-zinc-800">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </TooltipProvider>
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-medium">{editing ? "Editar Aviso" : "Novo Aviso"}</DialogTitle>
            <DialogDescription className="text-sm text-zinc-400">
              Defina o conteúdo, a validade e a imagem do comunicado.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="notice-form">
            <div className="flex items-center justify-between rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2.5">
              <Label htmlFor="notice-active" className="text-sm text-zinc-300">Ativo</Label>
              <Switch id="notice-active" data-testid="notice-active-input" checked={form.active}
                onCheckedChange={(v) => setForm({ ...form, active: v })} />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Título</Label>
              <Input data-testid="notice-title-input" required value={form.title} maxLength={120}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Ex.: Manutenção da piscina"
                className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 placeholder:text-zinc-500 rounded-md focus:border-indigo-500" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Texto do aviso</Label>
              <Textarea data-testid="notice-text-input" required value={form.text} rows={4} maxLength={2000}
                onChange={(e) => setForm({ ...form, text: e.target.value })}
                placeholder="Descreva o comunicado para os moradores/clientes…"
                className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 placeholder:text-zinc-500 rounded-md focus:border-indigo-500" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-zinc-400">Válido a partir de</Label>
                <Input data-testid="notice-valid-from-input" type="datetime-local" required value={form.valid_from}
                  onChange={(e) => setForm({ ...form, valid_from: e.target.value })}
                  className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md focus:border-indigo-500 [color-scheme:dark]" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Válido até</Label>
                <Input data-testid="notice-valid-until-input" type="datetime-local" required value={form.valid_until}
                  onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
                  className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md focus:border-indigo-500 [color-scheme:dark]" />
              </div>
            </div>

            <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3 space-y-3" data-testid="notice-image-section">
              <p className="text-xs font-medium text-zinc-400">Inserir Imagem</p>
              {form.image_path ? (
                <div className="relative">
                  <div className="w-full aspect-square max-w-xs mx-auto rounded-md border border-zinc-700 shadow-lg shadow-black/40 bg-zinc-900 overflow-hidden flex items-center justify-center">
                    <img src={fileUrl(form.image_path)} alt="Pré-visualização do aviso" data-testid="notice-image-preview"
                      className="max-w-full max-h-full object-contain" />
                  </div>
                  <Button type="button" data-testid="notice-image-remove" variant="ghost" size="icon" title="Remover imagem"
                    onClick={() => setForm({ ...form, image_path: null })}
                    className="absolute top-2 right-2 h-7 w-7 bg-zinc-950/80 text-zinc-300 hover:text-rose-400 hover:bg-zinc-900">
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="w-full h-28 rounded-md border border-dashed border-zinc-700 flex items-center justify-center text-zinc-600"
                  data-testid="notice-image-placeholder">
                  <ImagePlus className="h-6 w-6" />
                </div>
              )}
              <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="notice-image-file-input" onChange={pickImage} />
              <Button type="button" data-testid="notice-image-pick-button" variant="outline" disabled={uploading}
                onClick={() => fileRef.current?.click()}
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-zinc-100 rounded-md">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <ImagePlus className="h-4 w-4 mr-1.5" />}
                Buscar Imagem
              </Button>
              {form.image_path && (
                <div>
                  <Label className="text-xs text-zinc-400">Uso da imagem</Label>
                  <Select value={form.image_mode} onValueChange={(v) => setForm({ ...form, image_mode: v })}>
                    <SelectTrigger data-testid="notice-image-mode-select" className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                      <SelectItem data-testid="notice-image-mode-include" value="include">{IMAGE_MODE_LABEL.include}</SelectItem>
                      <SelectItem data-testid="notice-image-mode-download" value="download">{IMAGE_MODE_LABEL.download}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div>
              <Label className="text-xs text-zinc-400">Exibição</Label>
              <Select value={form.display} onValueChange={(v) => { setForm({ ...form, display: v }); if (v === "eql") setEqlPickerOpen(true); }}>
                <SelectTrigger data-testid="notice-display-select" className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                  <SelectItem data-testid="notice-display-todos" value="todos">Todos</SelectItem>
                  <SelectItem data-testid="notice-display-eql" value="eql">EQL</SelectItem>
                  <SelectItem data-testid="notice-display-bloco" value="bloco">Bloco</SelectItem>
                </SelectContent>
              </Select>
              {form.display === "eql" && (
                <button type="button" data-testid="notice-eql-open-button" onClick={() => setEqlPickerOpen(true)}
                  className="mt-2 w-full text-left text-xs text-indigo-400 hover:text-indigo-300 bg-zinc-950 border border-dashed border-zinc-700 hover:border-indigo-500/50 rounded-md px-3 py-2 transition-colors duration-200">
                  {form.eqls.length ? `${form.eqls.length} EQL(s) selecionada(s) — clique para alterar` : "Selecionar EQLs…"}
                </button>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" data-testid="notice-cancel-button" variant="ghost" onClick={() => setOpen(false)}
                className="text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 rounded-md">
                Cancelar
              </Button>
              <Button data-testid="notice-save-button" type="submit" disabled={saving || uploading}
                className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition-colors duration-200">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewing} onOpenChange={(v) => !v && setViewing(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100" data-testid="notice-view-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-medium">Pré-visualização do Aviso</DialogTitle>
            <DialogDescription className="text-sm text-zinc-400">
              {viewing?.image_mode === "download" ? "Formato com imagem disponível para download." : "Formato exibido em dispositivos móveis."}
            </DialogDescription>
          </DialogHeader>
          {viewing && viewing.image_mode === "download" ? (
            <div className="space-y-4">
              <h3 className="text-base font-medium text-zinc-100" data-testid="notice-view-title">{viewing.title}</h3>
              <p className="text-sm text-zinc-300 whitespace-pre-wrap" data-testid="notice-view-text">{viewing.text}</p>
              {viewing.image_path && (
                <Button data-testid="notice-download-image-button" onClick={() => downloadImage(viewing)}
                  className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition-colors duration-200">
                  <Download className="h-4 w-4 mr-1.5" /> Baixar Imagem
                </Button>
              )}
            </div>
          ) : viewing && (
            <div className="mx-auto w-full max-w-[300px] rounded-2xl border border-zinc-700 bg-zinc-950 shadow-xl shadow-black/50 overflow-hidden"
              data-testid="notice-view-mobile-card">
              {viewing.image_path && (
                <div className="w-full aspect-square bg-zinc-900 flex items-center justify-center">
                  <img src={fileUrl(viewing.image_path)} alt={viewing.title} data-testid="notice-view-image"
                    className="max-w-full max-h-full object-contain" />
                </div>
              )}
              <div className="p-4 space-y-2">
                <h3 className="text-base font-medium text-zinc-100" data-testid="notice-view-title">{viewing.title}</h3>
                <p className="text-sm text-zinc-300 whitespace-pre-wrap" data-testid="notice-view-text">{viewing.text}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={eqlFilterOpen} onOpenChange={setEqlFilterOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100" data-testid="notice-filter-eql-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-medium">Filtrar por EQL</DialogTitle>
            <DialogDescription className="text-sm text-zinc-400">
              Selecione a unidade para listar apenas os avisos direcionados a ela.
            </DialogDescription>
          </DialogHeader>
          <SearchableSelect onSelect={(eql) => { setFilterEql(eql); setEqlFilterOpen(false); }} />
        </DialogContent>
      </Dialog>

      <Dialog open={eqlPickerOpen} onOpenChange={setEqlPickerOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100" data-testid="notice-eql-picker">
          <DialogHeader>
            <DialogTitle className="text-lg font-medium">Selecionar EQLs</DialogTitle>
            <DialogDescription className="text-sm text-zinc-400">
              Busque e adicione as unidades que verão este aviso.
            </DialogDescription>
          </DialogHeader>
          <SearchableSelect excludeIds={form.eqls.map((e) => e.id)}
            onSelect={(eql) => setForm((f) => f.eqls.some((e) => e.id === eql.id) ? f : { ...f, eqls: [...f.eqls, eql] })} />
          {form.eqls.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3" data-testid="notice-eql-selected">
              {form.eqls.map((e) => (
                <span key={e.id} data-testid={`notice-eql-chip-${e.id}`}
                  className="inline-flex items-center gap-1 text-xs bg-indigo-600/20 text-indigo-300 rounded-full px-2 py-1">
                  {eqlLabel(e)}
                  <button type="button" data-testid={`notice-eql-remove-${e.id}`} title="Remover EQL"
                    onClick={() => setForm((f) => ({ ...f, eqls: f.eqls.filter((x) => x.id !== e.id) }))}
                    className="hover:text-rose-400 transition-colors duration-150">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="flex justify-end pt-1">
            <Button type="button" data-testid="notice-eql-confirm" onClick={() => setEqlPickerOpen(false)}
              className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition-colors duration-200">
              Concluir
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
          <DialogHeader>
            <DialogTitle className="text-lg font-medium">Excluir aviso</DialogTitle>
            <DialogDescription className="text-sm text-zinc-400">
              Tem certeza que deseja excluir <span className="text-zinc-200 font-medium">{deleteTarget?.title}</span>? Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2" data-testid="delete-notice-dialog">
            <Button data-testid="delete-notice-cancel" variant="ghost" onClick={() => setDeleteTarget(null)}
              className="text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 rounded-md">
              Cancelar
            </Button>
            <Button data-testid="delete-notice-confirm" onClick={deleteNotice} disabled={deleting}
              className="bg-rose-600 hover:bg-rose-500 text-white rounded-md transition-colors duration-200">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Excluir"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
